import { Phone, Users, ShieldCheck, ArrowRight, Mail } from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900">Quick Dial</span>
            <span className="text-xs text-gray-400 hidden sm:inline">by Simplify Office</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="#install" className="text-sm text-purple-600 hover:text-purple-700 font-medium hidden sm:inline">
              For IT admins →
            </a>
            <a href="/home" className="text-sm px-3 py-1.5 rounded border border-gray-200 hover:border-purple-300 hover:bg-purple-50 font-medium text-gray-900">
              Sign in
            </a>
          </div>
        </div>
      </header>
      <SignedOutNotice />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900 mb-5">
          Centrally managed contact directory<br className="hidden sm:inline" /> for Microsoft Teams
        </h1>
        <p className="max-w-2xl mx-auto text-lg text-gray-600 mb-8">
          Stop pinning the same numbers in every Teams chat. Quick Dial gives every user
          a curated list of people, queues, and shortcuts — published by IT, personalised
          by them.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="#install"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium"
          >
            Install in your tenant
            <ArrowRight className="w-4 h-4" />
          </a>
          <a href="/home" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 font-medium text-gray-900">
            Already using Quick Dial? Sign in
          </a>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Feature
            icon={<Users className="w-5 h-5 text-purple-600" />}
            title="Group-based publishing"
            body="IT publishes contact lists once. Each user sees the entries their Microsoft Entra group memberships entitle them to — no duplication, no stale spreadsheets."
          />
          <Feature
            icon={<Phone className="w-5 h-5 text-purple-600" />}
            title="Personal entries"
            body="Every user adds their own quick-dial shortcuts on top of what IT pushes. Private, synced across devices, kept inside the same UI."
          />
          <Feature
            icon={<ShieldCheck className="w-5 h-5 text-purple-600" />}
            title="Audit trail"
            body="Every change is logged with who, what, and when. Multi-tenant by design — your data lives in your partition and never crosses tenant boundaries."
          />
        </div>
      </section>

      {/* For IT admins */}
      <section id="install" className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">For IT administrators</h2>
        <p className="text-gray-600 mb-6">
          Quick Dial is a multi-tenant Microsoft Teams app. To deploy it in your tenant,
          your Global Administrator consents once via a standard Microsoft 365 admin
          consent flow — no service principal handoff, no infrastructure to install on
          your side.
        </p>

        <ol className="space-y-3 text-sm text-gray-700 mb-8">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center">1</span>
            <span>Contact us. We send your Global Administrator a one-click consent link.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center">2</span>
            <span>The first admin to open Quick Dial — in Teams, or in the browser at <span className="font-mono">/setup</span> — completes a one-time setup wizard, picking which Microsoft Entra security group represents your Quick Dial administrators.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center justify-center">3</span>
            <span>Your administrators publish contact entries to groups; everyone in the tenant sees their entries inside Teams and, with the same sign-in, in the browser at your organisation's own web address.</span>
          </li>
        </ol>

        <a
          href="mailto:louk@trackyour.it?subject=Quick%20Dial%20deployment%20enquiry"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 font-medium text-gray-900"
        >
          <Mail className="w-4 h-4" />
          louk@trackyour.it
        </a>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-12">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <div>© 2026 Track Your IT. Quick Dial is a Simplify Office product.</div>
          <div className="flex gap-4">
            <a href="mailto:louk@trackyour.it" className="hover:text-gray-700">Contact</a>
            <a href="/home" className="hover:text-gray-700">Sign in →</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}

function SignedOutNotice() {
  if (new URLSearchParams(window.location.search).get('signedout') !== '1') return null;
  return (
    <div className="bg-green-50 border-b border-green-200 text-green-800 text-sm text-center px-6 py-2">
      You're signed out. <a href="/home" className="underline">Sign in again</a>
    </div>
  );
}
